import {
  calculateDeliveryProposalOfferCargoStatus,
  calculateOfferCargoProcessStatus,
} from "@/features/offer-cargo-process-status";
import { calculateDeliveryProposalResponseDeadlineAt } from "@/features/offer-cargo-process-status/delivery-proposal-response-deadline-at";
import {
  calculateLatestRejectedContract,
  getOfferCargoRejectionReasonLabel,
} from "@/features/offer-cargo-rejection";
import { assertNever } from "@/shared/lib/assertNever";
import { excludeNull } from "@/shared/lib/collection";
import {
  fromIso8601DateTime,
  toLocaleTextAsDateTime,
} from "@/shared/lib/datetime";
import { BasePaper } from "@/shared/ui/base-paper";
import type {
  OfferCargoStatusCardCarrierCargoDetailVariantProps,
  OfferCargoStatusCardProps,
  OfferCargoStatusCardShipperCargoDetailVariantProps,
  OfferCargoStatusCardShipperDeliveryProposalDetailVariantProps,
} from "../model";
import type { ActionButtonProps } from "./action-buttons";
import {
  type OfferCargoStatusContentProps,
  OfferCargoStatusContents,
} from "./offer-cargo-status-contents";
import type { StatusTitleProps } from "./status-title";

const OfferCargoStatusCardCargoDetailVariant = (
  props:
    | ({
        viewer: "shipper";
      } & OfferCargoStatusCardShipperCargoDetailVariantProps)
    | ({
        viewer: "carrier";
      } & OfferCargoStatusCardCarrierCargoDetailVariantProps),
) => {
  const offerCargoProcessStatus = calculateOfferCargoProcessStatus({
    process: props.offerCargoProcess,
  });
  const receptionDeadlineAtDate = fromIso8601DateTime(
    props.offerCargoProcess.draftOfferCargo.deadlineAt,
  );
  const receptionStatusContent: OfferCargoStatusContentProps = (() => {
    const statusTitle: StatusTitleProps = {
      title:
        props.viewer === "shipper" ? "提案受付状況" : "この荷物の提案受付状況",
      viewer: props.viewer,
      statusType: "offer_cargo_process_reception",
      offerCargoProcessStatus: offerCargoProcessStatus,
    };

    let alerts: OfferCargoStatusContentProps["alerts"] = [];
    let texts: OfferCargoStatusContentProps["texts"] = [];
    let actionButtons: ActionButtonProps[] = [];

    switch (props.viewer) {
      case "shipper": {
        const receptionDeadlineAtDateTexts = [
          {
            text: `受付期限：${toLocaleTextAsDateTime(
              receptionDeadlineAtDate,
            )}`,
          },
        ];
        switch (offerCargoProcessStatus) {
          case "no_valid_delivery_proposals":
            texts = [
              ...receptionDeadlineAtDateTexts,
              {
                text: "※ 停止は画面下部から可能です",
                color: "grey.500",
              },
            ];
            actionButtons = [];
            break;
          case "reception_suspended":
          case "has_ongoing_proposal_with_no_contract":
            texts = receptionDeadlineAtDateTexts;
            actionButtons = [];
            break;
          case "has_contracts_all_rejected": {
            const latestRejectedContract = calculateLatestRejectedContract(
              props.offerCargoProcess,
            );
            const sourceOfferCargoOfRejectedContract =
              props.offerCargoProcess.deliveryProposalOfferCargos.find(
                (deliveryProposalOfferCargo) =>
                  deliveryProposalOfferCargo.id ===
                  latestRejectedContract?.sourceOfferCargoId,
              );
            const rejectionReason = latestRejectedContract?.rejection?.reason
              ? getOfferCargoRejectionReasonLabel(
                  latestRejectedContract.rejection.reason,
                )
              : undefined;
            const rejectedCompanyName =
              sourceOfferCargoOfRejectedContract?.company?.name;
            texts = receptionDeadlineAtDateTexts;
            alerts = [
              {
                variant: "warning",
                title: `${rejectedCompanyName}様に見送られました`,
                subTitle: `【見送りの理由：${rejectionReason}】`,
                text: latestRejectedContract?.rejection?.details ?? undefined,
              },
            ];
            actionButtons = [
              {
                label: "再度契約作成",
                variant: "contained",
                disabled: false,
                onClick: props.onRecreateContractClick,
              },
            ];
            break;
          }
          case "has_contract_pending":
          case "proposal_reception_deadline_expired":
          case "has_contract_concluded":
            texts = receptionDeadlineAtDateTexts;
            actionButtons = [];
            break;
        }
        break;
      }
      case "carrier": {
        texts = [
          {
            text: `受付期限：${toLocaleTextAsDateTime(
              receptionDeadlineAtDate,
            )}`,
          },
        ];
        const proposeActionButton: ActionButtonProps = {
          label: "配送提案する",
          variant: "contained",
          disabled: false,
          onClick: props.onProposeClick,
        };
        const disabledProposeActionButton: ActionButtonProps = {
          label: "配送提案済み",
          variant: "contained",
          disabled: true,
        };

        const myDeliveryProposal =
          props.offerCargoProcess.deliveryProposalOfferCargos.find(
            (proposal) => proposal.id === props.deliveryProposalOfferCargoId,
          );
        const deliveryProposalOfferCargoStatus = myDeliveryProposal?.id
          ? calculateDeliveryProposalOfferCargoStatus({
              deliveryProposalOfferCargoId: myDeliveryProposal.id,
              process: props.offerCargoProcess,
            })
          : undefined;
        switch (offerCargoProcessStatus) {
          case "no_valid_delivery_proposals":
            actionButtons =
              deliveryProposalOfferCargoStatus === "delivery_proposal_rejected"
                ? []
                : [proposeActionButton];
            break;
          case "reception_suspended":
            actionButtons = [];
            break;
          case "has_ongoing_proposal_with_no_contract":
          case "has_contracts_all_rejected":
          case "has_contract_pending":
            actionButtons =
              deliveryProposalOfferCargoStatus === "delivery_proposal_rejected"
                ? []
                : myDeliveryProposal
                  ? [disabledProposeActionButton]
                  : [proposeActionButton];
            break;
          case "proposal_reception_deadline_expired":
          case "has_contract_concluded":
            actionButtons = myDeliveryProposal
              ? [disabledProposeActionButton]
              : [];
            break;
        }
        break;
      }
      default: {
        assertNever(props);
        throw new Error(`unkown viewer: ${props}`);
      }
    }
    return {
      statusTitle,
      alerts,
      texts,
      actionButtons,
    };
  })();
  const contractStatusContent: OfferCargoStatusContentProps | null = (() => {
    if (props.viewer === "shipper") {
      return null;
    }
    const myDeliveryProposalOfferCargo =
      props.offerCargoProcess.deliveryProposalOfferCargos.find(
        (proposal) => proposal.id === props.deliveryProposalOfferCargoId,
      );
    if (!myDeliveryProposalOfferCargo) {
      return null;
    }
    const deliveryProposalOfferCargoStatus =
      calculateDeliveryProposalOfferCargoStatus({
        deliveryProposalOfferCargoId: myDeliveryProposalOfferCargo.id,
        process: props.offerCargoProcess,
      });
    const statusTitle: StatusTitleProps = {
      title: "契約状況",
      viewer: props.viewer,
      statusType: "delivery_proposal_contract",
      deliveryProposalOfferCargoStatus,
    };
    const texts: OfferCargoStatusContentProps["texts"] = [];
    let alerts: OfferCargoStatusContentProps["alerts"] = [];
    let actionButtons: ActionButtonProps[] = [];
    switch (deliveryProposalOfferCargoStatus) {
      case "no_valid_contracts":
        break;
      case "delivery_proposal_rejected": {
        const rejectionReason = myDeliveryProposalOfferCargo.rejection?.reason
          ? getOfferCargoRejectionReasonLabel(
              myDeliveryProposalOfferCargo.rejection.reason,
            )
          : undefined;
        const rejectedCompanyName =
          props.offerCargoProcess.draftOfferCargo.company?.name;
        alerts = [
          {
            variant: "warning",
            title: `${rejectedCompanyName}様に見送られました`,
            subTitle: `【見送りの理由：${rejectionReason}】`,
            text: myDeliveryProposalOfferCargo?.rejection?.details ?? undefined,
          },
        ];
        actionButtons = [];
        switch (offerCargoProcessStatus) {
          case "reception_suspended":
            break;
          case "no_valid_delivery_proposals":
          case "has_ongoing_proposal_with_no_contract":
          case "has_contracts_all_rejected":
          case "has_contract_pending":
            actionButtons = [
              {
                label: "再度提案する",
                variant: "outlined",
                disabled: false,
                onClick: props.onProposeAgainClick,
              },
            ];
            break;
          case "proposal_reception_deadline_expired":
          case "has_contract_concluded":
            break;
          default:
            assertNever(offerCargoProcessStatus);
            throw new Error(
              `unkown offerCargoProcessStatus: ${offerCargoProcessStatus}`,
            );
        }
        break;
      }
      case "has_contracts_all_rejected":
        break;
      case "has_contract_pending":
        switch (offerCargoProcessStatus) {
          case "reception_suspended":
          case "no_valid_delivery_proposals":
          case "has_ongoing_proposal_with_no_contract":
          case "has_contracts_all_rejected":
            break;
          case "has_contract_pending":
            actionButtons = [
              {
                label: "合意する",
                variant: "contained",
                disabled: false,
                onClick: props.onApproveContractClick,
              },
              {
                label: "見送る",
                variant: "outlined",
                disabled: false,
                onClick: props.onRejectContractClick,
              },
            ];
            break;
          case "proposal_reception_deadline_expired":
          case "has_contract_concluded":
            break;
          default:
            assertNever(offerCargoProcessStatus);
            throw new Error(
              `unkown offerCargoProcessStatus: ${offerCargoProcessStatus}`,
            );
        }
        break;
      case "delivery_proposal_response_deadline_expired":
        break;
      case "has_contract_concluded":
        switch (offerCargoProcessStatus) {
          case "reception_suspended":
          case "no_valid_delivery_proposals":
          case "has_ongoing_proposal_with_no_contract":
          case "has_contracts_all_rejected":
          case "has_contract_pending":
          case "proposal_reception_deadline_expired":
          case "has_contract_concluded":
            break;
          default:
            assertNever(offerCargoProcessStatus);
            throw new Error(
              `unkown offerCargoProcessStatus: ${offerCargoProcessStatus}`,
            );
        }
        break;
      default:
        assertNever(deliveryProposalOfferCargoStatus);
        throw new Error(
          `unkown deliveryProposalOfferCargoStatus: ${deliveryProposalOfferCargoStatus}`,
        );
    }
    return {
      statusTitle,
      alerts,
      texts,
      actionButtons,
    };
  })();
  return (
    <OfferCargoStatusContents
      statusContents={excludeNull([
        receptionStatusContent,
        contractStatusContent,
      ])}
    />
  );
};

const OfferCargoStatusCardDeliveryProposalDetailVariant = (
  props: {
    viewer: "shipper";
  } & OfferCargoStatusCardShipperDeliveryProposalDetailVariantProps,
) => {
  const { viewer, offerCargoProcess, deliveryProposalOfferCargoId } = props;
  const deliveryProposalOfferCargo =
    offerCargoProcess.deliveryProposalOfferCargos.find(
      (deliveryProposalOfferCargo) =>
        deliveryProposalOfferCargo.id === deliveryProposalOfferCargoId,
    );
  if (!deliveryProposalOfferCargo) {
    throw new Error(
      `deliveryProposalOfferCargo not found: ${deliveryProposalOfferCargoId}`,
    );
  }
  const offerCargoProcessStatus = calculateOfferCargoProcessStatus({
    process: offerCargoProcess,
  });
  const deliveryProposalStatus = calculateDeliveryProposalOfferCargoStatus({
    deliveryProposalOfferCargoId,
    process: offerCargoProcess,
  });
  const proposedAtDate = fromIso8601DateTime(
    deliveryProposalOfferCargo.createdAt,
  );
  const responseDeadlineAtDate = calculateDeliveryProposalResponseDeadlineAt(
    deliveryProposalOfferCargo,
  );

  const responseStatusContent: OfferCargoStatusContentProps = (() => {
    const statusTitle: StatusTitleProps = {
      title: "契約状況",
      viewer,
      statusType: "delivery_proposal_contract",
      deliveryProposalOfferCargoStatus: deliveryProposalStatus,
    };

    const texts: OfferCargoStatusContentProps["texts"] = [
      {
        color: viewer === "shipper" ? "#EA583B" : undefined,
        text: `返答期限：${toLocaleTextAsDateTime(responseDeadlineAtDate)}`,
      },
      {
        text: `提案日時：${toLocaleTextAsDateTime(proposedAtDate)}`,
      },
    ];
    let alerts: OfferCargoStatusContentProps["alerts"] = [];
    let actionButtons: ActionButtonProps[] = [];
    switch (props.viewer) {
      case "shipper": {
        switch (deliveryProposalStatus) {
          case "no_valid_contracts": {
            switch (offerCargoProcessStatus) {
              case "reception_suspended":
              case "no_valid_delivery_proposals":
                break;
              case "has_ongoing_proposal_with_no_contract":
              case "has_contracts_all_rejected":
                actionButtons = [
                  {
                    label: "契約作成・確認依頼に進む",
                    variant: "contained",
                    disabled: false,
                    onClick: props.onCreateContractClick,
                  },
                  {
                    label: "見送る",
                    variant: "outlined",
                    disabled: false,
                    onClick: props.onRejectClick,
                  },
                ];
                break;
              case "has_contract_pending":
                actionButtons = [
                  {
                    label: "契約確認依頼済み",
                    variant: "contained",
                    disabled: true,
                  },
                ];
                break;
              case "proposal_reception_deadline_expired":
                break;
              case "has_contract_concluded":
                actionButtons = [
                  {
                    label: "契約確認依頼済み",
                    variant: "contained",
                    disabled: true,
                  },
                ];
                break;
              default:
                assertNever(offerCargoProcessStatus);
                throw new Error(
                  `unkown offerCargoProcessStatus: ${offerCargoProcessStatus}`,
                );
            }
            break;
          }
          case "delivery_proposal_rejected":
            break;
          case "has_contracts_all_rejected": {
            const latestRejectedContract = calculateLatestRejectedContract(
              props.offerCargoProcess,
            );
            const rejectionReason = latestRejectedContract?.rejection?.reason
              ? getOfferCargoRejectionReasonLabel(
                  latestRejectedContract.rejection.reason,
                )
              : undefined;
            const rejectedCompanyName =
              props.offerCargoProcess.draftOfferCargo.company?.name;
            alerts = [
              {
                variant: "warning",
                title: `${rejectedCompanyName}様に見送られました`,
                subTitle: `【見送りの理由：${rejectionReason}】`,
                text: latestRejectedContract?.rejection?.details ?? undefined,
              },
            ];
            actionButtons = [
              {
                label: "再度契約作成",
                variant: "contained",
                disabled: false,
                onClick: props.onRecreateContractClick,
              },
            ];
            switch (offerCargoProcessStatus) {
              case "reception_suspended":
              case "no_valid_delivery_proposals":
              case "has_ongoing_proposal_with_no_contract":
                break;
              case "has_contracts_all_rejected":
                actionButtons = [
                  {
                    label: "再度契約作成",
                    variant: "contained",
                    disabled: false,
                    onClick: props.onRecreateContractClick,
                  },
                ];
                break;
              case "has_contract_pending":
                actionButtons = [
                  {
                    label: "契約確認依頼済み",
                    variant: "contained",
                    disabled: true,
                  },
                ];
                break;
              case "proposal_reception_deadline_expired":
              case "has_contract_concluded":
                break;
              default:
                assertNever(offerCargoProcessStatus);
                throw new Error(
                  `unkown offerCargoProcessStatus: ${offerCargoProcessStatus}`,
                );
            }
            break;
          }
          case "has_contract_pending":
            break;
          case "delivery_proposal_response_deadline_expired":
            switch (offerCargoProcessStatus) {
              case "reception_suspended":
              case "no_valid_delivery_proposals":
                break;
              case "has_ongoing_proposal_with_no_contract":
              case "has_contracts_all_rejected":
              case "has_contract_pending":
              case "proposal_reception_deadline_expired":
                actionButtons = [
                  {
                    label: "返答期限を過ぎています",
                    variant: "contained",
                    disabled: true,
                  },
                ];
                break;
              case "has_contract_concluded":
                break;
              default:
                assertNever(offerCargoProcessStatus);
                throw new Error(
                  `unkown offerCargoProcessStatus: ${offerCargoProcessStatus}`,
                );
            }
            break;
          case "has_contract_concluded":
            switch (offerCargoProcessStatus) {
              case "reception_suspended":
                actionButtons = [
                  {
                    label: "契約内容を確認",
                    variant: "outlined",
                    disabled: false,
                    onClick: props.onConfirmContractClick,
                  },
                ];
                break;
              case "no_valid_delivery_proposals":
              case "has_ongoing_proposal_with_no_contract":
              case "has_contracts_all_rejected":
              case "has_contract_pending":
              case "proposal_reception_deadline_expired":
                break;
              case "has_contract_concluded":
                actionButtons = [
                  {
                    label: "配送依頼をアップロード",
                    variant: "contained",
                    disabled: false,
                    // TODO:
                    // onClick: props.onUploadDeliveryRequestClick,
                  },
                ];
                break;
              default:
                assertNever(offerCargoProcessStatus);
                throw new Error(
                  `unkown offerCargoProcessStatus: ${offerCargoProcessStatus}`,
                );
            }
            break;
          default:
            assertNever(deliveryProposalStatus);
            throw new Error(
              `unkown deliveryProposalStatus: ${deliveryProposalStatus}`,
            );
        }
        break;
      }
    }
    return {
      statusTitle,
      alerts,
      texts,
      actionButtons,
    };
  })();
  return <OfferCargoStatusContents statusContents={[responseStatusContent]} />;
};

export const OfferCargoStatusCard = (props: OfferCargoStatusCardProps) => {
  return (
    <BasePaper
      variant="elevation"
      sx={{ alignSelf: "flex-start", width: { xs: "100%", sm: "auto " } }}
    >
      {props.variant === "shipper_cargo_detail" ? (
        <OfferCargoStatusCardCargoDetailVariant viewer="shipper" {...props} />
      ) : props.variant === "shipper_delivery_proposal_detail" ? (
        <OfferCargoStatusCardDeliveryProposalDetailVariant
          viewer="shipper"
          {...props}
        />
      ) : props.variant === "carrier_cargo_detail" ? (
        <OfferCargoStatusCardCargoDetailVariant viewer="carrier" {...props} />
      ) : null}
    </BasePaper>
  );
};
